import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Search, TrendingUp, MapPin, Home, BarChart3, ArrowRight, Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '@/propertyai/components/ui/button';
import { Card } from '@/propertyai/components/ui/card';
import { Badge } from '@/propertyai/components/ui/badge';
import PropertyCard from '@/propertyai/components/PropertyCard';
import { getFeaturedProperties, getMarketStats, getCities } from '@/propertyai/lib/api';

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [featRes, statsRes, citiesRes] = await Promise.all([
        getFeaturedProperties(),
        getMarketStats(),
        getCities(),
      ]);
      setFeatured(featRes.data.properties || []);
      setStats(statsRes.data);
      setCities(citiesRes.data.cities || []);
    } catch (e) {
      console.error('Failed to load landing data', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/property-ai/search?query=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/property-ai/search');
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_10%,hsl(34_65%_92%)_0%,transparent_55%),radial-gradient(circle_at_80%_0%,hsl(186_72%_90%)_0%,transparent_50%)] dark:opacity-5" />
        <div className="relative max-w-3xl mx-auto text-center">
          <Badge variant="secondary" className="mb-4 text-xs">
            <Sparkles className="w-3 h-3 mr-1" /> AI-Powered Real Estate
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-4 font-['Space_Grotesk']">
            Find Your Perfect Home
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
            Search 1,500+ properties across 10 metros with AI-powered valuations and a conversational advisor.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="flex items-center gap-2 bg-background/90 backdrop-blur-sm border border-border rounded-xl shadow-[0_10px_30px_-20px_hsl(var(--shadow-color)/0.15)] p-1.5">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder='Try "3-bed homes under $500K in Austin with a pool"'
                  className="flex-1 bg-transparent border-none outline-none text-sm py-2"
                  data-testid="nl-search-input"
                />
              </div>
              <Button type="submit" className="rounded-xl px-6" data-testid="nl-search-submit-button">
                Search
              </Button>
            </div>
          </form>

          {/* City chips */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {cities.slice(0, 6).map(city => (
              <button
                key={city.name}
                onClick={() => navigate(`/property-ai/search?city=${city.name}`)}
                className="flex items-center gap-1.5 rounded-full border border-border bg-background/80 px-3 py-1.5 text-xs hover:bg-secondary transition-colors"
              >
                <MapPin className="w-3 h-3" />
                {city.name}, {city.state}
                <span className="text-muted-foreground">({city.count})</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Market Stats */}
      {stats && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="rounded-2xl p-5 bg-background border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Home className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums font-['Space_Grotesk']">
                    {stats.total_active?.toLocaleString()}
                  </p>
                  <p className="text-xs text-muted-foreground">Active Listings</p>
                </div>
              </div>
            </Card>
            <Card className="rounded-2xl p-5 bg-background border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums font-['Space_Grotesk']">
                    ${stats.city_stats?.[0]?.avg_price?.toLocaleString() || '0'}
                  </p>
                  <p className="text-xs text-muted-foreground">Avg. Price ({stats.city_stats?.[0]?.city})</p>
                </div>
              </div>
            </Card>
            <Card className="rounded-2xl p-5 bg-background border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-semibold tabular-nums font-['Space_Grotesk']">
                    {stats.city_stats?.[0]?.avg_days_on_market || 0} days
                  </p>
                  <p className="text-xs text-muted-foreground">Avg. Days on Market</p>
                </div>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Featured Listings */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl sm:text-2xl font-semibold">Featured Listings</h2>
            <p className="text-sm text-muted-foreground mt-1">Handpicked properties across top metros</p>
          </div>
          <Button variant="ghost" className="rounded-xl gap-1" onClick={() => navigate('/property-ai/search')}>
            View all <ArrowRight className="w-4 h-4" />
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="rounded-2xl border border-border bg-background overflow-hidden">
                <div className="aspect-[16/10] shimmer" />
                <div className="p-4 space-y-2">
                  <div className="h-5 shimmer rounded w-1/3" />
                  <div className="h-4 shimmer rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.slice(0, 6).map(prop => (
              <PropertyCard key={prop.id} property={prop} />
            ))}
          </div>
        )}
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="text-xl sm:text-2xl font-semibold text-center mb-10">How PropertyAI Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { icon: Search, title: 'Search Naturally', desc: 'Type what you want in plain English. Our AI understands "3-bed homes near good schools under $500K".' },
            { icon: BarChart3, title: 'AI Valuations', desc: 'Get instant property value estimates powered by machine learning, trained on real market data.' },
            { icon: Sparkles, title: 'Ask AI Advisor', desc: 'Chat with our AI about affordability, neighborhoods, offers, and market trends.' },
          ].map(({ icon: Icon, title, desc }, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* City Stats */}
      {stats?.city_stats && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <h2 className="text-xl sm:text-2xl font-semibold mb-6">Market Overview by City</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {stats.city_stats.map(city => (
              <Card
                key={city.city}
                className="rounded-xl p-4 border border-border bg-background hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/property-ai/search?city=${city.city}`)}
              >
                <h4 className="font-medium text-sm">{city.city}, {city.state}</h4>
                <p className="text-lg font-semibold tabular-nums font-['Space_Grotesk'] mt-1">
                  ${city.avg_price?.toLocaleString()}
                </p>
                <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                  <span>{city.active_listings} listings</span>
                  <span>{city.avg_days_on_market}d avg DOM</span>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-border mt-10 py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-primary flex items-center justify-center">
              <Home className="w-3 h-3 text-primary-foreground" />
            </div>
            <span className="font-medium text-foreground">PropertyAI</span>
          </div>
          <p>AI-powered real estate intelligence platform. Data is simulated for demonstration.</p>
          <Link
            to="/marketplace"
            className="flex items-center gap-1.5 rounded-xl border border-border bg-background px-4 py-2 text-sm text-foreground hover:bg-secondary transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to Marketplace
          </Link>
        </div>
      </footer>
    </div>
  );
}

